import React, { useEffect } from "react";
import "./styles/App.css";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import SideNav from "./components/navigation/MainNavigation";
import Home from "./pages/Home";
import PipingSchedule from "./pages/PipingSchedule";
import { Toaster } from "react-hot-toast";
import { PublicClientApplication } from "@azure/msal-browser";
import {
  MsalProvider,
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
  useIsAuthenticated,
} from "@azure/msal-react";
import { msalConfig } from "./config/authConfig";
import SignInButton from "./components/navigation/SignInButton";
import { setMsalInstance } from "./services/api/apiClient";
import { appInsights } from "./services/analytics/appInsights";
import Settings from "./pages/Settings";
import DuctSchedule from "./pages/DuctSchedule";

// initialize msal instance for authentication
const msalInstance = new PublicClientApplication(msalConfig);

// Component to handle page tracking and user authentication tracking
function AppInsightsTracker() {
  const location = useLocation();
  const isAuthenticated = useIsAuthenticated();
  const { accounts } = useMsal();

  // Track page views
  useEffect(() => {
    if (appInsights && process.env.NODE_ENV === "production") {
      appInsights.trackPageView({
        name: location.pathname,
        uri: window.location.href,
      });
    }
  }, [location]);

  // Track authenticated users
  useEffect(() => {
    if (
      isAuthenticated &&
      accounts.length > 0 &&
      appInsights &&
      process.env.NODE_ENV === "production"
    ) {
      // User is authenticated - set user context
      appInsights.trackEvent({
        name: "UserAlreadyAuthenticated",
        properties: {
          userId: accounts[0].homeAccountId,
          tenantId: accounts[0].tenantId,
          userPrincipalName: accounts[0].username,
          timestamp: new Date().toISOString(),
          currentPage: location.pathname,
        },
      });

      // Set user context for all future telemetry
      appInsights.setAuthenticatedUserContext(
        accounts[0].localAccountId || accounts[0].homeAccountId,
        accounts[0].tenantId
      );
    } else if (
      !isAuthenticated &&
      appInsights &&
      process.env.NODE_ENV === "production"
    ) {
      // User is not authenticated - clear user context
      appInsights.clearAuthenticatedUserContext();
    }
  }, [isAuthenticated, accounts, location]);

  return null; // This component doesn't render anything
}

// Component to initialize the apiClient with MSAL instance for authentication
function MsalInitializer() {
  const { instance } = useMsal();

  useEffect(() => {
    setMsalInstance(instance);
  }, [instance]);

  return null;
}

// Main app content that needs to be inside Router
function AppContent() {
  return (
    <>
      <AppInsightsTracker />
      <MsalInitializer />
      <AuthenticatedTemplate>
        <div className="App">
          <SideNav />
          <Toaster />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/piping-schedule" element={<PipingSchedule />} />
            <Route path="/duct-schedule" element={<DuctSchedule />} />
            <Route path="/settings" element={<Settings />} />
          </Routes>
        </div>
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <div className="fixed inset-0 flex items-center justify-center bg-gray-50">
          <div className="max-w-md w-full space-y-8 p-8">
            <div>
              <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
                MEP PM Tools
              </h2>
            </div>
            <div className="mt-8 space-y-6">
              <div className="flex justify-center">
                <SignInButton />
              </div>
            </div>
          </div>
        </div>
      </UnauthenticatedTemplate>
    </>
  );
}

function App() {
  return (
    <MsalProvider instance={msalInstance}>
      {/* <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}> */}
      <Router>
        <AppContent />
      </Router>
    </MsalProvider>
  );
}

export default App;
